/**
 * Template Orchestrator Service
 * Coordinates background analysis, text fitting, logo processing, and constraint-aware content generation
 */

import { analyzeBackground } from "../utils/backgroundAnalysis";
import {
  processLogoForBackground,
  canProcessLogo
} from "../utils/logoProcessor";
import { fitTextToConstraints } from "../utils/textFitting";
import { getTemplateConstraints } from "../constants/templateConstraints";

/**
 * Get default background analysis when actual analysis fails
 * @param {'dark' | 'light' | 'adaptive'} backgroundStyle
 * @returns {Object}
 */
function getDefaultBackgroundAnalysis(backgroundStyle) {
  if (backgroundStyle === "dark") {
    return {
      brightness: 50,
      isDark: true,
      recommendedTextColor: "#FFFFFF",
      recommendedLogoMode: "white"
    };
  } else if (backgroundStyle === "light") {
    return {
      brightness: 200,
      isDark: false,
      recommendedTextColor: "#000000",
      recommendedLogoMode: "black"
    };
  } else {
    // adaptive default
    return {
      brightness: 128,
      isDark: false,
      recommendedTextColor: "#000000",
      recommendedLogoMode: "color"
    };
  }
}

/**
 * Determine logo mode based on background and supported modes
 * @param {Object} bgAnalysis
 * @param {string[]} supportedModes
 * @returns {'white' | 'black' | 'color'}
 */
function determineLogoMode(bgAnalysis, supportedModes) {
  const recommended = bgAnalysis.recommendedLogoMode;

  // If recommended mode is supported, use it
  if (supportedModes.includes(recommended)) {
    return recommended;
  }

  // Otherwise, use first supported mode
  return supportedModes[0] || "color";
}

/**
 * Determine text color based on background
 * @param {Object} bgAnalysis
 * @param {'dark' | 'light' | 'adaptive'} backgroundStyle
 * @returns {string}
 */
function determineTextColor(bgAnalysis, backgroundStyle) {
  // If template has explicit background style, use it
  if (backgroundStyle === "dark") {
    return "#FFFFFF";
  } else if (backgroundStyle === "light") {
    return "#000000";
  }

  // Otherwise, use analysis recommendation
  return bgAnalysis.recommendedTextColor;
}

/**
 * Prepare template with constraint-aware dynamic content
 * @param {string} templateId - Template ID
 * @param {string} backgroundImageUrl - Background image URL
 * @param {Object} generatedContent - AI-generated content (headlines, descriptions, etc.)
 * @param {Object} brandDetails - Brand details (logo, colors, fonts)
 * @returns {Promise<Object>} Enhanced template data ready for Polotno
 */
export async function prepareTemplateContent(
  templateId,
  backgroundImageUrl,
  generatedContent,
  brandDetails
) {
  console.log(`🎨 Orchestrating template: ${templateId}`);

  // Debug: Log what we received
  console.log(`📦 Received data:`, {
    hasBackgroundUrl: !!backgroundImageUrl,
    hasGeneratedContent: !!generatedContent,
    generatedContentType: typeof generatedContent,
    generatedContentKeys: generatedContent ? Object.keys(generatedContent) : [],
    hasBrandDetails: !!brandDetails
  });

  // 1. Get template constraints
  const constraints = getTemplateConstraints(templateId);
  if (!constraints) {
    console.warn(`⚠️ No constraints found for template: ${templateId}`);
    return null;
  }

  // 2. Analyze background image
  let bgAnalysis;
  try {
    bgAnalysis = await analyzeBackground(backgroundImageUrl);
    console.log(`📊 Background analysis:`, {
      brightness: bgAnalysis.brightness.toFixed(2),
      isDark: bgAnalysis.isDark,
      recommendedTextColor: bgAnalysis.recommendedTextColor,
      recommendedLogoMode: bgAnalysis.recommendedLogoMode
    });
  } catch (error) {
    console.error("Background analysis failed:", error);
    // Fallback to template's default background style
    bgAnalysis = getDefaultBackgroundAnalysis(constraints.backgroundStyle);
  }

  // 3. Fit text to constraints
  const fittedText = {};
  const textZones = constraints.textZones;

  // Safety check: Ensure generatedContent is an object
  const safeGeneratedContent = generatedContent || {};

  if (textZones.heading) {
    const headlineText =
      safeGeneratedContent.headlines?.[0] ||
      safeGeneratedContent.primaryText?.[0] ||
      "Your Headline";

    fittedText.heading = fitTextToConstraints(
      headlineText,
      {
        ...textZones.heading,
        width: textZones.heading.position.width,
        height: textZones.heading.position.height
      },
      brandDetails?.theme?.headingFont || "Inter"
    );

    if (fittedText.heading.needsRewrite) {
      console.warn(
        `⚠️ Heading needs rewrite: ${fittedText.heading.rewriteReason}`
      );
    }
  }

  if (textZones.subheading) {
    const subheadingText =
      safeGeneratedContent.headlines?.[1] ||
      safeGeneratedContent.description?.[0] ||
      "Your Subheading";

    fittedText.subheading = fitTextToConstraints(
      subheadingText,
      {
        ...textZones.subheading,
        width: textZones.subheading.position.width,
        height: textZones.subheading.position.height
      },
      brandDetails?.theme?.bodyFont || "Inter"
    );

    if (fittedText.subheading.needsRewrite) {
      console.warn(
        `⚠️ Subheading needs rewrite: ${fittedText.subheading.rewriteReason}`
      );
    }
  }

  if (textZones.cta) {
    const ctaText = safeGeneratedContent.callToAction || "Learn More";

    fittedText.cta = fitTextToConstraints(
      ctaText,
      {
        ...textZones.cta,
        width: textZones.cta.position.width,
        height: textZones.cta.position.height
      },
      brandDetails?.theme?.bodyFont || "Inter"
    );
  }

  // 4. Process logo
  let processedLogoUrl = brandDetails.theme?.logo;
  const logoUrl = brandDetails.theme?.logo;

  if (logoUrl && canProcessLogo(logoUrl)) {
    try {
      const logoMode = determineLogoMode(
        bgAnalysis,
        constraints.logoZone.colorModes
      );

      processedLogoUrl = await processLogoForBackground(logoUrl, logoMode);
      console.log(`✅ Logo processed to ${logoMode} mode`);
    } catch (error) {
      console.error("Logo processing failed:", error);
      // Keep original logo
    }
  }

  // 5. Determine text colors based on background
  const textColor = determineTextColor(bgAnalysis, constraints.backgroundStyle);

  // 6. Return processed data
  return {
    templateId,
    constraints,
    backgroundAnalysis: bgAnalysis,
    fittedText,
    processedLogoUrl,
    textColor,
    brandColor: brandDetails.theme?.brandColors?.[0] || "#000000",
    needsRewrite: Object.values(fittedText).some((t) => t.needsRewrite)
  };
}

/**
 * Check if any text needs AI rewrite
 * @param {Object} orchestratedData - Result from prepareTemplateContent
 * @returns {Array<Object>} Array of rewrite requests
 */
export function getRewriteRequests(orchestratedData) {
  const requests = [];
  const { fittedText, constraints } = orchestratedData;

  Object.keys(fittedText).forEach((zone) => {
    if (fittedText[zone].needsRewrite) {
      const zoneConstraints = constraints.textZones[zone];
      requests.push({
        zone,
        originalText: fittedText[zone].text,
        reason: fittedText[zone].rewriteReason,
        constraints: {
          maxLines: zoneConstraints.maxLines,
          maxCharsPerLine: zoneConstraints.maxCharsPerLine,
          maxTotalChars:
            zoneConstraints.maxLines * zoneConstraints.maxCharsPerLine
        }
      });
    }
  });

  return requests;
}

export default {
  prepareTemplateContent,
  getRewriteRequests
};
